import cx from 'classnames';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import type { InputSize } from '../Input/styles';
import Spinner from '../Spinner';

export type ButtonSize = InputSize;

// Match Input heights while allowing longer button labels to wrap.
const sizeClassNameMap: Record<ButtonSize, string> = {
  sm: 'min-h-7 px-3 py-0.5 text-sm leading-normal',
  md: 'min-h-10 px-4 py-1 text-base leading-normal',
  lg: 'min-h-14 px-4 py-2 text-base leading-normal',
};

interface ButtonProps {
  /* button size; md by default */
  size?: ButtonSize;
  /* children */
  children: React.ReactNode;
  /* custom classes */
  className?: string;
  /* disable */
  disabled?: boolean;
  /* click handler */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /* tab order */
  tabIndex?: string;
  /* button type */
  type?: 'button' | 'submit' | 'reset' | undefined;
  /* button variant - see styleguide */
  variant?: 'primary' | 'secondary' | 'neutral' | 'success' | 'critical' | 'warning' | undefined;
  /* if true, shows a spinner */
  loading?: boolean;
  fullWidth?: boolean;
  href?: string;
  style?: React.CSSProperties;
  testID?: string; // used for tests
  icon?: React.ReactNode;
  title?: string;
  onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
}

const baseClassName = 'relative rounded-md inline-flex items-center justify-center border outline-none';

const primaryButtonClasses = cx([
  'text-white dark:text-dark-base',
  'bg-control-primary',
  // Keep hover stronger than a caller's legacy dark background override.
  'hover:bg-control-primary-hover dark:hover:bg-control-primary-hover',
  'border-transparent',
  'focus:bg-light-control-primary-hover dark:focus:bg-dark-selected',
  'active:bg-light-control-primary-hover dark:active:bg-dark-selected',
  'focus:border-control-focus',
]);

const secondaryButtonClasses = cx([
  'text-default',
  'bg-control-secondary',
  'hover:bg-light-control-secondary-hover dark:hover:bg-dark-hover',
  'focus:bg-light-control-secondary-hover dark:focus:bg-dark-shade',
  'border-control',
  'focus:border-control-hover',
]);

const neutralButtonClasses = cx([
  'text-default',
  'bg-grey-light-4 dark:bg-dark-control-secondary',
  'hover:bg-grey-light-5 dark:hover:bg-dark-control-secondary-hover',
  'focus:bg-grey-light-5 dark:focus:bg-dark-control-secondary-hover',
  'active:bg-grey-light-5 dark:active:bg-dark-control-secondary-hover',
  'border-transparent',
  'shadow-sm',
]);

const successButtonClasses = cx([
  'dark:text-dark-base',
  'bg-alert-success',
  'hover:bg-alert-success-hover',
  'border-transparent',
  'focus:bg-alert-success-hover',
  'active:bg-alert-success-hover',
]);

const criticalButtonClasses = cx([
  'dark:text-dark-base',
  'bg-alert-critical',
  'hover:bg-alert-critical-hover',
  'border-transparent',
  'focus:bg-alert-critical-hover',
  'active:bg-alert-critical-hover',
]);

const warningButtonClasses = cx([
  'dark:text-dark-base',
  'bg-alert-warning',
  'hover:bg-alert-warning-hover',
  'border-transparent',
  'focus:bg-alert-warning-hover',
  'active:bg-alert-warning-hover',
]);

const Button = ({
  children,
  className: classNameProp,
  disabled,
  onClick,
  type,
  size = 'md',
  tabIndex,
  loading,
  fullWidth,
  href,
  style,
  testID,
  icon,
  title,
  variant = 'primary',
  onFocus,
}: ButtonProps) => {
  const history = useHistory();

  const themeClassNameMap: Record<string, string> = {
    primary: primaryButtonClasses,
    secondary: secondaryButtonClasses,
    neutral: neutralButtonClasses,
    success: successButtonClasses,
    critical: criticalButtonClasses,
    warning: warningButtonClasses,
  };

  const className = cx([
    baseClassName,
    themeClassNameMap[variant],
    sizeClassNameMap[size],
    disabled ? 'opacity-25' : loading ? 'opacity-75' : '',
    disabled ? 'pointer-events-none select-none' : 'cursor-pointer',
    fullWidth && 'w-full',
    classNameProp,
  ]);

  const Element = href ? 'a' : 'button';

  return (
    <Element
      className={className}
      disabled={disabled}
      onClick={useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (href) {
            event.preventDefault();
            if (disabled) return;
            history.push(href);
          }
          onClick?.(event);
        },
        [history, href, onClick, disabled],
      )}
      tabIndex={tabIndex ? parseInt(tabIndex) : undefined}
      type={type}
      href={href}
      style={style}
      data-testid={testID}
      title={title}
      onFocus={onFocus}
    >
      <span className={`flex items-center ${loading ? 'opacity-0' : ''}`}>
        {icon && <span className="mr-4">{icon}</span>}
        {children}
      </span>
      {loading && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            display: 'flex',
          }}
        >
          <Spinner color={variant !== 'primary' ? 'tt-blue' : 'white'} size="sm" />
        </div>
      )}
    </Element>
  );
};

export default Button;
